import React, { useCallback, useEffect, useState } from 'react';
import ConfirmModal from '../../../Common/ConfirmModal';
import {
  getRecordingsBackupStatus,
  testRecordingsBackupConnection,
  registerRecordingsBackup,
  rebindRecordingsBackup,
  rotateRecordingsBackupKey,
  runRecordingsBackupNow,
  updateRecordingsBackupSettings,
} from '../../../../services';
import {
  cloneBanner,
  diskClass,
  formatAge,
  formatBytes,
  messageForCode,
  stateView,
  validateBackupHost,
  validateBwlimit,
  validateHostName,
  validatePort,
  validateSchedule,
  validateToken,
} from './recordingsBackupScreen';
import './RecordingsBackup.css';

const EMPTY_FORM = { backupHost: '200.174.142.40', backupPort: '22', hostName: '', token: '' };

const RecordingsBackup = () => {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [settings, setSettings] = useState({ bwlimitKbps: '20000', deleteAfterSync: true, scheduleTime: '02:21' });
  const [mode, setMode] = useState('register');
  const [notice, setNotice] = useState('');
  const [connection, setConnection] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);

  const applyStatus = (data) => {
    setStatus(data);
    if (data && data.settings) {
      setSettings({
        bwlimitKbps: String(data.settings.bwlimitKbps),
        deleteAfterSync: Boolean(data.settings.deleteAfterSync),
        scheduleTime: data.settings.scheduleTime || '02:21',
      });
    }
  };

  const reload = useCallback(async () => {
    const result = await getRecordingsBackupStatus();
    if (!result.success) {
      setNotice(messageForCode(result.code, result.error));
      return;
    }
    const data = result.data;
    setStatus(data);
    if (data && data.settings) {
      setSettings({
        bwlimitKbps: String(data.settings.bwlimitKbps),
        deleteAfterSync: Boolean(data.settings.deleteAfterSync),
        scheduleTime: data.settings.scheduleTime || '02:21',
      });
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (!status || !status.syncRunning) return undefined;
    const timer = setInterval(reload, 10000);
    return () => clearInterval(timer);
  }, [status, reload]);

  const setField = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submitRegistration = async (event) => {
    event.preventDefault();
    const token = form.token;
    setForm((current) => ({ ...current, token: '' }));
    if (mode === 'rebind') {
      const tokenError = validateToken(token);
      if (tokenError) { setNotice(tokenError); return; }
      const result = await rebindRecordingsBackup({ token });
      if (!result.success) { setNotice(messageForCode(result.code, result.error)); return; }
      setNotice('');
      applyStatus(result.data);
      return;
    }
    const problems = [
      validateBackupHost(form.backupHost),
      validatePort(Number(form.backupPort)),
      validateHostName(form.hostName),
      validateToken(token),
    ].find(Boolean);
    if (problems) { setNotice(problems); return; }
    const result = await registerRecordingsBackup({
      backupHost: form.backupHost,
      backupPort: Number(form.backupPort),
      hostName: form.hostName,
      token,
    });
    if (!result.success) { setNotice(messageForCode(result.code, result.error)); return; }
    setNotice('');
    applyStatus(result.data);
  };

  const testConnection = async () => {
    const result = await testRecordingsBackupConnection({
      backupHost: form.backupHost,
      backupPort: Number(form.backupPort),
    });
    if (!result.success) { setNotice(messageForCode(result.code, result.error)); return; }
    setConnection(result.data);
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    const problems = validateBwlimit(Number(settings.bwlimitKbps)) || validateSchedule(settings.scheduleTime);
    if (problems) { setNotice(problems); return; }
    const result = await updateRecordingsBackupSettings({
      bwlimitKbps: Number(settings.bwlimitKbps),
      deleteAfterSync: settings.deleteAfterSync,
      scheduleTime: settings.scheduleTime,
    });
    setNotice(result.success ? 'Configuração salva.' : messageForCode(result.code, result.error));
  };

  const sendNow = async () => {
    const result = await runRecordingsBackupNow();
    if (!result.success) { setNotice(messageForCode(result.code, result.error)); return; }
    setStatus((current) => ({ ...(current || {}), syncRunning: true }));
    reload();
  };

  const rotate = async () => {
    setConfirmRotate(false);
    const result = await rotateRecordingsBackupKey();
    setNotice(result.success ? 'Chave trocada.' : messageForCode(result.code, result.error));
    if (result.success) applyStatus(result.data);
  };

  const copyInstance = async () => {
    if (!status || !status.instanceId || !navigator.clipboard) return;
    await navigator.clipboard.writeText(status.instanceId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const view = stateView(status && status.state);
  const registered = status && status.state === 'REGISTERED';
  const lastRun = status && status.lastRun ? status.lastRun : {};

  return (
    <section className="recordings-backup">
      <header className="recordings-backup-header">
        <h1 className="title is-4">Backup de gravações</h1>
        <p>Sugestão de nome: &lt;cliente&gt;-&lt;local&gt;-&lt;nn&gt;</p>
      </header>
      {notice ? <div className="notification is-warning" role="alert">{notice}</div> : null}
      <div className="recordings-backup-grid">
        <article className="card recordings-backup-card">
          <header className="card-header"><p className="card-header-title">Identidade</p></header>
          <div className="card-content">
            <p>Host: {status && status.hostName ? status.hostName : '—'}</p>
            <p>
              Instance ID: <code>{status && status.instanceId ? status.instanceId : '—'}</code>
              <button type="button" className="button is-small is-light" onClick={copyInstance}>Copiar</button>
              {copied ? <span> copiado</span> : null}
            </p>
            <p className="recordings-backup-state" style={{ color: view.color }}>{view.label}</p>
            {status && status.weakFingerprint ? <p className="recordings-backup-weak">Impressão digital fraca. O clone pode não ser detectado.</p> : null}
          </div>
        </article>
        <article className={`card recordings-backup-card ${registered ? 'is-hidden' : ''}`}>
          <header className="card-header"><p className="card-header-title">Registro</p></header>
          <div className="card-content">
            {status && status.state === 'CLONE_DETECTED' ? (
              <div className="buttons">
                <button type="button" className={`button ${mode === 'register' ? 'is-link' : ''}`} onClick={() => setMode('register')}>Registrar como novo host</button>
                <button type="button" className={`button ${mode === 'rebind' ? 'is-link' : ''}`} onClick={() => setMode('rebind')}>Re-vincular</button>
              </div>
            ) : null}
            {status && status.state === 'CLONE_DETECTED' ? <p>{cloneBanner(status.hostName)}</p> : null}
            <form onSubmit={submitRegistration}>
              <div className={mode === 'rebind' ? 'is-hidden' : ''}>
                <label className="label">Servidor<input className="input" value={form.backupHost} onChange={setField('backupHost')} /></label>
                <label className="label">Porta<input className="input" value={form.backupPort} onChange={setField('backupPort')} /></label>
                <label className="label">Nome<input className="input" value={form.hostName} onChange={setField('hostName')} /></label>
              </div>
              <label className="label">Token<input className="input" type="password" value={form.token} onChange={setField('token')} autoComplete="off" /></label>
              <div className="buttons">
                <button type="button" className="button" onClick={testConnection}>Testar conexão</button>
                <button type="submit" className="button is-link">{mode === 'rebind' ? 'Re-vincular' : 'Registrar'}</button>
              </div>
            </form>
            {connection ? (
              <ul>
                <li>TCP: {connection.tcp ? 'ok' : 'falhou'}</li>
                <li>Host key: {connection.hostKey ? 'ok' : 'falhou'}</li>
                <li>Matrícula: {connection.enrollReachable ? 'ok' : 'falhou'}</li>
                <li>Latência: {connection.latencyMs} ms</li>
              </ul>
            ) : null}
          </div>
        </article>
        <article className="card recordings-backup-card">
          <header className="card-header"><p className="card-header-title">Operação</p></header>
          <div className="card-content">
            <form onSubmit={saveSettings}>
              <label className="label">Horário<input className="input" value={settings.scheduleTime} onChange={(event) => setSettings({ ...settings, scheduleTime: event.target.value })} /></label>
              <label className="label">Limite de banda (KB/s)<input className="input" value={settings.bwlimitKbps} onChange={(event) => setSettings({ ...settings, bwlimitKbps: event.target.value })} /></label>
              <label className="checkbox">
                <input type="checkbox" checked={settings.deleteAfterSync} onChange={(event) => setSettings({ ...settings, deleteAfterSync: event.target.checked })} />
                Apagar cópia local após envio
              </label>
              {!settings.deleteAfterSync ? <p className="recordings-backup-danger-note">A cópia local será mantida e o disco pode encher.</p> : null}
              <button type="submit" className="button is-link">Salvar</button>
            </form>
          </div>
        </article>
        <article className="card recordings-backup-card">
          <header className="card-header"><p className="card-header-title">Saúde</p></header>
          <div className="card-content">
            <p>Última execução: {formatAge(status && status.lastOkAt)}</p>
            <p>Arquivos: {lastRun.files || 0} · {formatBytes(lastRun.bytes)}</p>
            <progress className={`progress recordings-backup-progress ${diskClass(status && status.localDiskPct)}`} value={Number(status && status.localDiskPct) || 0} max="100" />
            <p>Backlog: {status && status.backlogTruncated ? '200000+' : (status ? status.backlogFiles : 0)}</p>
            <ul className="recordings-backup-events">
              {((status && status.recentEvents) || []).map((line) => <li key={line}>{line}</li>)}
            </ul>
          </div>
        </article>
      </div>
      <div className="buttons" style={{ marginTop: '1rem' }}>
        <button type="button" className="button is-primary" onClick={sendNow} disabled={!registered}>Enviar agora</button>
        <button type="button" className="button" onClick={() => setConfirmRotate(true)} disabled={!registered}>Trocar chave</button>
      </div>
      <ConfirmModal
        open={confirmRotate}
        title="Trocar chave"
        body="A chave atual deixa de valer assim que a nova for aceita pelo backup. Deseja continuar?"
        confirmLabel="Trocar chave"
        cancelLabel="Cancelar"
        danger
        onCancel={() => setConfirmRotate(false)}
        onConfirm={rotate}
      />
    </section>
  );
};

export default RecordingsBackup;
